import React, { useState, useEffect } from "react";
import { LoanStatusDetails } from "../../interface/MembersPortal/ILoan";
import helpers from "../../helpers/index";
import SignatureWithNameModal from "../Signature/SignatureWithNameModal";

interface ApplicationForLoanFormProps {
    loanId?: string;
    loanStatusDetails: LoanStatusDetails | null;
    onGmSignatureChange?: (signature: string, name: string) => void;
    onCreditCommittee1Change?: (signature: string, name: string) => void;
    onCreditCommittee2Change?: (signature: string, name: string) => void;
    onCreditCommittee3Change?: (signature: string, name: string) => void;
}

function ApplicationForLoanForm({ loanId, loanStatusDetails, onGmSignatureChange, onCreditCommittee1Change, onCreditCommittee2Change, onCreditCommittee3Change }: ApplicationForLoanFormProps) {

    const [isGMSignModalOpen, setIsGMSignModalOpen] = useState(false);
    const [gmSignature, setGmSignature] = useState<string>('');
    const [gmSigneeName, setGmSigneeName] = useState<string>('');
    const [isHoveringGM, setIsHoveringGM] = useState(false);

    const [isCC1SignModalOpen, setIsCC1SignModalOpen] = useState(false);
    const [cc1Signature, setCc1Signature] = useState<string>('');
    const [cc1SigneeName, setCc1SigneeName] = useState<string>('');
    const [isHoveringCC1, setIsHoveringCC1] = useState(false);

    const [isCC2SignModalOpen, setIsCC2SignModalOpen] = useState(false);
    const [cc2Signature, setCc2Signature] = useState<string>('');
    const [cc2SigneeName, setCc2SigneeName] = useState<string>('');
    const [isHoveringCC2, setIsHoveringCC2] = useState(false);

    const [isCC3SignModalOpen, setIsCC3SignModalOpen] = useState(false);
    const [cc3Signature, setCc3Signature] = useState<string>('');
    const [cc3SigneeName, setCc3SigneeName] = useState<string>('');
    const [isHoveringCC3, setIsHoveringCC3] = useState(false);

    useEffect(function () {
        if (loanStatusDetails) {
            setGmSignature(loanStatusDetails.gmSignature ?? '');
            setGmSigneeName(loanStatusDetails.gmFullname ?? '');
            setCc1Signature(loanStatusDetails.creditCommitteeSignature1 ?? '');
            setCc1SigneeName(loanStatusDetails.creditCommitteeFullname1 ?? '');
            setCc2Signature(loanStatusDetails.creditCommitteeSignature2 ?? '');
            setCc2SigneeName(loanStatusDetails.creditCommitteeFullname2 ?? '');
            setCc3Signature(loanStatusDetails.creditCommitteeSignature3 ?? '');
            setCc3SigneeName(loanStatusDetails.creditCommitteeFullname3 ?? '');
        }
    }, [loanStatusDetails]);

    const isMember = helpers.getCookieValue('role') === 'Member';
    const showGMSignButton = !isMember && loanStatusDetails?.loanStatus === 2 && (!(gmSignature && gmSigneeName) || isHoveringGM);
    const showCC1SignButton = !isMember && loanStatusDetails?.loanStatus === 2 && (!(cc1Signature && cc1SigneeName) || isHoveringCC1);
    const showCC2SignButton = !isMember && loanStatusDetails?.loanStatus === 2 && (!(cc2Signature && cc2SigneeName) || isHoveringCC2);
    const showCC3SignButton = !isMember && loanStatusDetails?.loanStatus === 2 && (!(cc3Signature && cc3SigneeName) || isHoveringCC3);

    const fullName = `${loanStatusDetails?.firstname ?? ''} ${loanStatusDetails?.lastname ?? ''}`.trim();

    const comaker1MiddleInitial = loanStatusDetails?.comakerMiddlename1 ? `${loanStatusDetails.comakerMiddlename1.charAt(0).toUpperCase()}.` : '';
    const comaker1FullName = [loanStatusDetails?.comakerFirstname1, comaker1MiddleInitial, loanStatusDetails?.comakerLastname1].filter(Boolean).join(' ');

    const comaker2MiddleInitial = loanStatusDetails?.comakerMiddlename2 ? `${loanStatusDetails.comakerMiddlename2.charAt(0).toUpperCase()}.` : '';
    const comaker2FullName = [loanStatusDetails?.comakerFirstname2, comaker2MiddleInitial, loanStatusDetails?.comakerLastname2].filter(Boolean).join(' ');

    const amountLoan = Number(loanStatusDetails?.amountLoan ?? 0);
    const term = loanStatusDetails?.term ?? 0;
    const amortization = Number(loanStatusDetails?.amortizationSchedule ?? 0);

    const fieldLine = (value: React.ReactNode, minWidth = '150px') => (
        <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth, textAlign: 'center', padding: '0 4px' }}>
            {value}
        </span>
    );

    const emptyLine = (minWidth = '150px') => (
        <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth }}>&nbsp;</span>
    );

    return (
        <>
        <div style={{ maxWidth: '820px', margin: '0 auto', fontFamily: 'Times New Roman, serif', fontSize: '13px', padding: '32px', background: '#fff', border: '1px solid #ccc' }}>

            {/* KLF No. label */}
            <div style={{ fontSize: '11px', marginBottom: '6px' }}>
                KLF No. 02-004 (LOANS P10,500 to P70,000.00)
            </div>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '18px' }}>
                <div style={{ fontWeight: 'bold' }}>KOOP ng mga KAWANI NG PAMAHALAANG</div>
                <div style={{ fontWeight: 'bold' }}>PANLALAWIGAN NG BATANGAN MPC</div>
                <div style={{ fontWeight: 'bold' }}>(KOOPBATANGAN MPC)</div>
                <br />
                <div style={{ fontWeight: 'bold', fontSize: '15px', textDecoration: 'underline', letterSpacing: '1px' }}>
                    APPLICATION FOR LOAN
                </div>
            </div>

            {/* Account Numbers & Department/Date */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div>
                    <span>Loan Account No. </span>
                    {fieldLine(loanId, '120px')}
                </div>
                <div style={{ textAlign: 'center' }}>
                    {fieldLine(loanStatusDetails?.departmentName ?? ' ', '200px')}
                    <div style={{ fontSize: '11px', textAlign: 'center' }}>Department/Office</div>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '18px' }}>
                <div>
                    <span>Savings Account No. </span>
                    {fieldLine(loanStatusDetails?.membershipId, '120px')}
                </div>
                <div style={{ textAlign: 'center' }}>
                    {emptyLine('200px')}
                    <div style={{ fontSize: '11px', textAlign: 'center' }}>Date</div>
                </div>
            </div>

            {/* Application Body */}
            <div style={{ marginBottom: '16px', lineHeight: '2.2' }}>
                <span>I, </span>
                {fieldLine(<strong>{fullName}</strong>, '220px')}
                <span>, hereby apply for a loan to KOOPBATANGAN MULTI-PURPOSE COOPERATIVE amounting to </span>
                {fieldLine(amountLoan.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), '160px')}
                <span> (P </span>
                {fieldLine(<strong>{amountLoan.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>, '120px')}
                <span>) to be paid in </span>
                {fieldLine(term, '60px')}
                <span> (</span>
                {fieldLine(term, '60px')}
                <span>) months in a semi - monthly installment of </span>
                {fieldLine(amortization.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), '130px')}
                <span> (</span>
                {fieldLine(amortization.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), '130px')}
                <span>)</span>
            </div>

            {/* Loan Purpose */}
            <div style={{ marginBottom: '10px' }}>
                <span>I, desire this loan for &nbsp;&nbsp;</span>
                <span style={{ marginRight: '24px' }}>
                    <input type="checkbox" disabled style={{ marginRight: '4px' }} />productive
                </span>
                <span>
                    <input type="checkbox" disabled style={{ marginRight: '4px' }} />provident purpose(s)
                </span>
            </div>

            <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                <span>1.</span>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ display: 'inline-block', textAlign: 'center', minWidth: '220px' }}>
                        {loanStatusDetails?.comakerSignature1 && (
                            <img src={loanStatusDetails.comakerSignature1} alt="" style={{ width: '220px', height: '60px', objectFit: 'contain', display: 'block' }} />
                        )}
                        <span style={{ display: 'block', borderBottom: '1px solid #000', fontSize: '12px', padding: '0 2px' }}>
                            {comaker1FullName || ' '}
                        </span>
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', marginBottom: '4px' }}>
                        <span>2.</span>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ display: 'inline-block', textAlign: 'center', minWidth: '220px' }}>
                                {loanStatusDetails?.comakerSignature2 && (
                                    <img src={loanStatusDetails.comakerSignature2} alt="" style={{ width: '220px', height: '60px', objectFit: 'contain', display: 'block' }} />
                                )}
                                <span style={{ display: 'block', borderBottom: '1px solid #000', fontSize: '12px', padding: '0 2px' }}>
                                    {comaker2FullName || ' '}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div style={{ fontSize: '11px', textAlign: 'center', marginLeft: '16px' }}>Name/Signature of Co-makers</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                    {loanStatusDetails?.memberSignature
                        ? <img id="memberSignature" src={loanStatusDetails.memberSignature} alt="" style={{ width: '200px', height: '60px', objectFit: 'contain', borderBottom: '1px solid #000', display: 'block' }} />
                        : <span id="memberSignature" style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '200px' }}>&nbsp;</span>
                    }
                    <div style={{ fontSize: '11px' }}>Applicant's Signature</div>
                </div>
            </div>

            {/* Recommending / Approved */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <div><strong>RECOMMENDING APPROVAL:</strong></div>
                <div><strong>APPROVED:</strong></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <div></div>
                <div
                    id="dvCreditCommittee1"
                    style={{ width: '220px', textAlign: 'center' }}
                    onMouseEnter={() => setIsHoveringCC1(true)}
                    onMouseLeave={() => setIsHoveringCC1(false)}
                >
                    <div style={{ position: 'relative', width: '220px', height: '60px', display: 'inline-block' }}>
                        {cc1Signature && (
                            <img src={cc1Signature} alt="" style={{ width: '220px', height: '60px', objectFit: 'contain', display: 'block' }} />
                        )}
                        {showCC1SignButton && (
                            <button
                                type="button"
                                onClick={() => setIsCC1SignModalOpen(true)}
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    fontSize: '11px',
                                    padding: '2px 10px',
                                    cursor: 'pointer',
                                }}
                            >Sign</button>
                        )}
                    </div>
                    <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '220px', textAlign: 'center', fontSize: '12px' }}>
                        {cc1SigneeName || <>&nbsp;</>}
                    </span>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <div></div>
                <div
                    id="dvCreditCommittee2"
                    style={{ width: '220px', textAlign: 'center' }}
                    onMouseEnter={() => setIsHoveringCC2(true)}
                    onMouseLeave={() => setIsHoveringCC2(false)}
                >
                    <div style={{ position: 'relative', width: '220px', height: '60px', display: 'inline-block' }}>
                        {cc2Signature && (
                            <img src={cc2Signature} alt="" style={{ width: '220px', height: '60px', objectFit: 'contain', display: 'block' }} />
                        )}
                        {showCC2SignButton && (
                            <button
                                type="button"
                                onClick={() => setIsCC2SignModalOpen(true)}
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    fontSize: '11px',
                                    padding: '2px 10px',
                                    cursor: 'pointer',
                                }}
                            >Sign</button>
                        )}
                    </div>
                    <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '220px', textAlign: 'center', fontSize: '12px' }}>
                        {cc2SigneeName || <>&nbsp;</>}
                    </span>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <div
                    id="dvGeneralManager"
                    style={{ width: '220px', textAlign: 'center' }}
                    onMouseEnter={() => setIsHoveringGM(true)}
                    onMouseLeave={() => setIsHoveringGM(false)}
                >
                    <div style={{ position: 'relative', width: '220px', height: '60px', display: 'inline-block' }}>
                        {gmSignature && (
                            <img src={gmSignature} alt="" style={{ width: '220px', height: '60px', objectFit: 'contain', display: 'block' }} />
                        )}
                        {showGMSignButton && (
                            <button
                                type="button"
                                onClick={() => setIsGMSignModalOpen(true)}
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    fontSize: '11px',
                                    padding: '2px 10px',
                                    cursor: 'pointer',
                                }}
                            >Sign</button>
                        )}
                    </div>
                    <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '220px', textAlign: 'center', fontSize: '12px' }}>
                        {gmSigneeName || <>&nbsp;</>}
                    </span>
                </div>
                <div
                    id="dvCreditCommittee3"
                    style={{ width: '220px', textAlign: 'center' }}
                    onMouseEnter={() => setIsHoveringCC3(true)}
                    onMouseLeave={() => setIsHoveringCC3(false)}
                >
                    <div style={{ position: 'relative', width: '220px', height: '60px', display: 'inline-block' }}>
                        {cc3Signature && (
                            <img src={cc3Signature} alt="" style={{ width: '220px', height: '60px', objectFit: 'contain', display: 'block' }} />
                        )}
                        {showCC3SignButton && (
                            <button
                                type="button"
                                onClick={() => setIsCC3SignModalOpen(true)}
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    fontSize: '11px',
                                    padding: '2px 10px',
                                    cursor: 'pointer',
                                }}
                            >Sign</button>
                        )}
                    </div>
                    <span style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '220px', textAlign: 'center', fontSize: '12px' }}>
                        {cc3SigneeName || <>&nbsp;</>}
                    </span>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', textAlign: 'center', width: '220px' }}>General Manager</div>
                <div style={{ fontSize: '11px', textAlign: 'center', width: '220px' }}>Credit Comittee</div>
            </div>

            <div style={{ fontSize: '11px', fontStyle: 'italic', marginBottom: '10px' }}>
                Note: Loan officer should sign initial on recommendation.
            </div>

            <hr style={{ border: 'none', borderTop: '2px dashed #000', marginBottom: '20px' }} />

            {/* Contract of Payment */}
            <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '15px', textDecoration: 'underline', letterSpacing: '1px', marginBottom: '16px' }}>
                CONTRACT OF PAYMENT
            </div>

            <div style={{ marginBottom: '16px', lineHeight: '2.2' }}>
                <span>I, </span>
                {fieldLine(<strong>{fullName}</strong>, '260px')}
                <span>, hereby agree to pay my loan to KOOPBATANGAN MPC based on the following terms and conditions:</span>
            </div>

            <ol style={{ marginBottom: '24px', lineHeight: '1.8', paddingLeft: '20px' }}>
                <li style={{ marginBottom: '10px' }}>
                    <span>That I am allowing to deduct thru payroll my monthly amortization in the amount of </span>
                    {fieldLine(amortization.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), '180px')}
                    <span> (P </span>
                    {fieldLine(amortization.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), '130px')}
                    <span>)</span>
                    <br />
                    <em>This serves also as waiver in effect.</em>
                </li>
                <li style={{ marginBottom: '10px' }}>
                    That I will pay a corresponding penalty of 2% in violation of the provision of existing policy and will affect my credit standing to the cooperative.
                </li>
                <li style={{ marginBottom: '10px' }}>
                    That I will abide with the provisions specified in the General Guidelines in KOOPBATANGAN Lending and KOOPBATANGAN Credit/Lending Policies and other existing provisions in R.A. 6938, and related laws, Articles of Cooperation and By-laws, rules and regulation, statutory laws and ordinances of Provincial Government of Batangas as well as LGU/Prov'l Treasurer KOOPBATANGAN Memorandum of Understanding (MOU) pertaining thereto.
                </li>
                <li style={{ marginBottom: '10px' }}>
                    That if at any time I will retire, I am authorizing KOOPBATANGAN to collect the balance of my loan from the monies that are due me from the <u>Provincial Government of Batangas.</u>
                </li>
            </ol>

            {/* Signatures */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px', marginTop: '32px' }}>
                <div style={{ textAlign: 'center' }}>
                    {loanStatusDetails?.memberSignature
                        ? <img id="memberSignature" src={loanStatusDetails.memberSignature} alt="" style={{ width: '200px', height: '60px', objectFit: 'contain', borderBottom: '1px solid #000', display: 'block' }} />
                        : <span id="memberSignature" style={{ borderBottom: '1px solid #000', display: 'inline-block', minWidth: '200px' }}>&nbsp;</span>
                    }
                    <div style={{ fontSize: '11px' }}>Applicant's Signature</div>
                </div>
                <div>
                    <div>Notary Public</div>
                </div>
            </div>

            {/* Notary fields */}
            <div style={{ fontSize: '12px', lineHeight: '2' }}>
                <div>Doc. No. &nbsp;{emptyLine('130px')}</div>
                <div>Page. No. {emptyLine('130px')}</div>
                <div>Book. No. {emptyLine('130px')}</div>
                <div>Series of {emptyLine('130px')}</div>
            </div>

        </div>

        <SignatureWithNameModal
            isOpen={isGMSignModalOpen}
            title="General Manager Signature"
            onSave={(image, name) => { setGmSignature(image); setGmSigneeName(name); setIsGMSignModalOpen(false); onGmSignatureChange?.(image, name); }}
            onCancel={() => setIsGMSignModalOpen(false)}
        />
        <SignatureWithNameModal
            isOpen={isCC1SignModalOpen}
            title="Credit Committee 1 Signature"
            onSave={(image, name) => { setCc1Signature(image); setCc1SigneeName(name); setIsCC1SignModalOpen(false); onCreditCommittee1Change?.(image, name); }}
            onCancel={() => setIsCC1SignModalOpen(false)}
        />
        <SignatureWithNameModal
            isOpen={isCC2SignModalOpen}
            title="Credit Committee 2 Signature"
            onSave={(image, name) => { setCc2Signature(image); setCc2SigneeName(name); setIsCC2SignModalOpen(false); onCreditCommittee2Change?.(image, name); }}
            onCancel={() => setIsCC2SignModalOpen(false)}
        />
        <SignatureWithNameModal
            isOpen={isCC3SignModalOpen}
            title="Credit Committee 3 Signature"
            onSave={(image, name) => { setCc3Signature(image); setCc3SigneeName(name); setIsCC3SignModalOpen(false); onCreditCommittee3Change?.(image, name); }}
            onCancel={() => setIsCC3SignModalOpen(false)}
        />
        </>
    );
}

export default ApplicationForLoanForm;
